import React, { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  ActivityTemplateForm,
  emptyActivityTemplateFormValues,
} from "../components/ActivityTemplateForm";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { activityTemplateDetailPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

export function AddActivityTemplatePage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getActivityTypes(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist,
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey="templates"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Add Activity Template"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              {
                label: "Activity Templates",
                path: "/clinician-portal/activity-templates",
              },
              { label: "Add Activity Template" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {state.status === "loading" ? (
          <LoadingState label="Loading Activity Template form" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Activity Template creation is unavailable")}
          />
        ) : null}
        {available && data ? (
          <ActivityTemplateForm
            mode="create"
            initialValues={emptyActivityTemplateFormValues}
            activityTypes={data.activityTypes}
            clinicianCode={clinicianCode}
            cancelPath="/clinician-portal/activity-templates"
            isSubmitting={mutation.state.status === "pending"}
            submissionError={
              mutation.state.status === "error"
                ? mutation.state.error
                : undefined
            }
            clearSubmissionError={mutation.reset}
            onSubmit={async (values) => {
              const result = await mutation.run(
                "create-activity-template",
                (signal) =>
                  clinicianPortalApi.createActivityTemplate(
                    clinicianCode,
                    values,
                    signal,
                  ),
              );
              if (result?.created && result.template) {
                navigateWithinPortal(
                  withClinicianCode(
                    activityTemplateDetailPath(result.template.id),
                    clinicianCode,
                  ),
                );
              }
            }}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
